<template>
    <section class="hero-container" :style="bannerStyle">
        <template v-if="!isMobileComputed">
            <div class="hero-image-wrapper" v-if="backgroundImage">
                <img
                    fetchpriority="high"
                    class="hero-image"
                    loading="eager"
                    :src="backgroundImage"
                    :alt="$t(title) || 'Hero Image'"
                />
            </div>
            <div class="hero-gradient" :style="gradientStyle"></div>
        </template>
        <div v-if="showToolbar" :style="titleProps" class="hero-tool-bar">
            <img
                :src="toolbarImage"
                alt="hero-banner-tool-bar"
                width="12"
                height="12"
            />
            <span>
                {{ $t('Last Updated') }}:
                <b>{{ getFormattedDate }}</b>
            </span>
        </div>
        <div class="text-content">
            <Typography v-bind="titleProps" class="title" v-html="$t(title)" />

            <Typography
                v-if="showSubtitle"
                v-bind="subTitleProps"
                class="sub-title"
                v-html="$t(subTitle)"
            />
        </div>
    </section>
</template>

<script>
import { heroBannerMixin, mediaQueriesMixin } from '@/mixins';
import { fontWeight } from '@/constants';
import { typographyTags } from '@atoms/Text/Typography';
import { HeroBannerBetaProps } from '@organisms/HeroBanners/HeroBannerBeta/HeroBannerBetaProps';

export default {
    name: 'HeroBannerBeta',
    mixins: [mediaQueriesMixin, heroBannerMixin],
    props: {
        title: {
            type: String,
        },
        subTitle: {
            type: String,
        },
        backgroundImage: {
            type: String,
        },
        ...HeroBannerBetaProps,
    },
    data() {
        return {
            typographyTags,
        };
    },
    computed: {
        isMobileComputed() {
            return this.isMobile();
        },
        showToolbar() {
            return (
                this.$globalConstants.host.last_updated_month &&
                this.isMobileComputed
            );
        },
        showSubtitle() {
            return !this.hideSubTitle && this.subTitle;
        },
        titleProps() {
            return {
                tag: this.typographyTags.h1,
                color: this.isMobileComputed
                    ? this.titleMobileColor
                    : this.titleColor,
                lineHeight: this.getTitleFontSize,
                size: this.getTitleFontSize,
                textAlign: this.titleAlign,
                weight: fontWeight.bold,
            };
        },
        subTitleProps() {
            return {
                tag: this.typographyTags.h2,
                color: this.isMobileComputed
                    ? this.subTitleMobileColor
                    : this.subTitleColor,
                lineHeight: '1.5',
                size: this.getSubTitleFontSize,
                textAlign: this.subTitleAlign,
            };
        },
        gradientStyle() {
            if (this.isMobileComputed) {
                return {};
            }
            return {
                background: `linear-gradient(to right, ${this.gradientColor}, ${this.gradientColor}, ${this.gradientColor}, ${this.gradientColor}, rgba(0, 0, 0, 0))`,
            };
        },
        bannerStyle() {
            if (this.isMobileComputed) {
                return {
                    height: this.getFixedHeight,
                    backgroundColor: `${this.mobileGradientColor}`,
                };
            }

            return {
                height: this.getFixedHeight,
                backgroundColor: 'transparent',
            };
        },
        toolbarImage() {
            return `${this.$globalConstants.imageUrl}/img/icons/rating-icons/tick.svg?d=12x12&q=60&color=008000FF`;
        },
        getFormattedDate() {
            const currentDate = new Date();
            const options = { month: 'long', year: 'numeric' };
            return currentDate.toLocaleDateString(
                window.__host__['lang'],
                options
            );
        },
    },
};
</script>

<style lang="scss" scoped>
@import '@/sass/mixins';
@import '@/sass/pad-width-variables';

.hero-container {
    margin-bottom: 0;
    display: flex;
    flex-direction: column;
    max-width: 1440px;
    position: relative;
    width: 100%;
    margin-left: auto;
    margin-right: auto;
    z-index: 2;
    background-position: center center;
    background-size: cover;
    background-repeat: no-repeat;
    justify-content: flex-start;
    overflow-x: hidden;
    @include mq('desktop') {
        min-height: 360px;
    }
    .hero-gradient {
        position: absolute;
        top: 0;
        left: 0;
        width: 80%;
        height: 100%;
    }

    .hero-image-wrapper {
        position: absolute;
        top: 0;
        left: 60%;
        width: 40%;
        height: 100%;
    }

    .hero-image {
        position: absolute;
        inset: 0;
        object-fit: cover;
        width: 100%;
        height: 100%;
    }

    .text-content {
        z-index: 1;
        width: 100%;
        padding: 2.5rem 20px 20px;
        max-width: 1000px;
        @include mq('tablet') {
            padding: 2.5rem 40px;
        }
        @include mq('desktop') {
            padding: 2.5rem 110px;
        }
    }

    .sub-title {
        margin-top: 20px;
        max-width: 780px;
    }

    .hero-tool-bar {
        left: 15px;
        top: 10px;
        font-size: 12px;
        color: white;
        position: absolute;
        img {
            background: white;
            border-radius: 7px;
        }
    }
}
</style>
